import React, { FC, useEffect, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { DealerPartExchangeProps } from '@components/DealerPartExchange/index';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import BasketSelectSection from '@components/v2/BasketSelectSection';
import { CUSTOM_PX_VALUES, isBrandAC, ModalVersion } from '../../constants/main';
import { DealService } from '../../services';
import { getSessionIdCookie } from '@utils/Session.utils';
import { CarDetailsDuck } from '../../redux/carDetails/carDetails.duck';
import FinanceWidgetDuck from '../../redux/financeWidget/financeWidget.duck';
import { useDispatch } from 'react-redux';
import UIDuck from '../../redux/commonDucks/ui.duck';
import { PERSONALIZE_OFFER_SECTIONS } from '@components/Modal/ModalPersonalizeOffer/ModalPersonalizeOfferTemplate';
import Button from '@components/Button';
import { IconTemplate } from '@components/Icon/IconTemplate';
import { Icons } from '@components/Icon';
import Spinner from '@components/Spinner';
import { theme } from '../../styles/theme';
import { useUserDuck } from '@hooks/useUserDuck';
import { usePersonalizedOffer } from '@hooks/usePersonalizedOffer';
import { useIgnorePXBonus } from '../../partExchange/hooks';
import { useGTM } from '@hooks/useGTM';

const DealerPartExchangeTemplate: FC<DealerPartExchangeProps> = ({ className }) => {
    const { t } = useTranslations();

    const dispatch = useDispatch();

    const { pushToDataLayer } = useGTM();

    const { formatMoney } = useMoneyFormatter();

    const { currentDeal } = useCarDetailsDuck();

    const { isUserLoggedInAsDealer } = useUserDuck();

    const { isPersonalizedDeal } = usePersonalizedOffer();

    const [isLoading, setIsLoading] = useState(false);
    const [customPx, setCustomPx] = useState(null);

    const { shouldIgnorePXBonus } = useIgnorePXBonus();

    useEffect(() => {
        setCustomPx(currentDeal?.extraFields?.px ? JSON.parse(currentDeal?.extraFields?.px) : null);
    }, [currentDeal]);

    const handleChangeSelection = async (value: string) => {
        pushToDataLayer({
            event: 'uaevent',
            eventCategory: 'Content',
            eventAction: 'Filter::TradeInOffer',
            eventLabel: value === CUSTOM_PX_VALUES.YES ? 'yes' : 'no',
        });

        if (value === CUSTOM_PX_VALUES.YES && !customPx) {
            return handleEditValues();
        }

        setIsLoading(true);

        const { data } = await DealService.updatePxStatus(value === CUSTOM_PX_VALUES.YES, getSessionIdCookie());

        setIsLoading(false);

        dispatch(CarDetailsDuck.setCurrentDealAndCarDetail(data.deal));
    };

    const handleEditValues = async () => {
        pushToDataLayer({
            event: 'uaevent',
            eventCategory: 'Content',
            eventAction: 'Display::EditConfigurationModal',
            eventLabel: t('modal.personalizeOffer.edit.button'),
        });

        return dispatch(
            UIDuck.openModal({
                data: {
                    openSection: PERSONALIZE_OFFER_SECTIONS.TRADE_IN,
                },
                modalType: UIDuck.MODAL_TYPES.PERSONALIZE_OFFER,
                modalVersion: ModalVersion.v2,
                modalProperties: {
                    withoutPadding: true,
                },
            })
        );
    };

    const addPersonalizeOfferSuffixToTranslationKey = (translationKey: string) => {
        if (isPersonalizedDeal) {
            return `${translationKey}.personalizedOffer`;
        }

        return translationKey;
    };

    return (
        <div className={className}>
            {isUserLoggedInAsDealer && (
                <BasketSelectSection
                    leftSectionTitle={t(
                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.selection.yes.title')
                    )}
                    leftSectionDescription={t(
                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.selection.yes.description')
                    )}
                    rightSectionTitle={t(
                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.selection.no.title')
                    )}
                    rightSectionDescription={t(
                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.selection.no.description')
                    )}
                    leftValue={CUSTOM_PX_VALUES.YES}
                    rightValue={CUSTOM_PX_VALUES.NO}
                    leftSelected={customPx?.vehicle?.isActive}
                    rightSelected={!customPx?.vehicle?.isActive}
                    onClick={handleChangeSelection}
                />
            )}
            {isLoading && <Spinner size={25} border={3} color={theme.colors.black} />}
            {!isLoading && customPx && (
                <>
                    <div className="detailTitle">
                        {t(addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.summary.title'))}
                    </div>
                    <div className="detail">
                        <div className="leftSection">
                            <p>{t(addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.summary.description'))}</p>
                        </div>
                        <div className="rightSection">
                            <p>
                                <span>
                                    {t(addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.summary.model'))}
                                </span>
                                <span className="value">
                                    {customPx?.vehicle?.makeName} {customPx?.vehicle?.modelName}
                                </span>
                            </p>
                            <p>
                                <span>
                                    {t(
                                        addPersonalizeOfferSuffixToTranslationKey(
                                            'basket.dealerPx.summary.overallMilleage'
                                        )
                                    )}
                                </span>
                                <span className="value">{customPx?.vehicle?.overallMilleage}</span>
                            </p>
                            {isUserLoggedInAsDealer && (
                                <div>
                                    <button onClick={handleEditValues}>
                                        {t(addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.summary.modify'))}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="summary">
                        <div className="leftSection">
                            <span className="summaryTitle">
                                {t(addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.summary.trade.title'))}
                            </span>
                            <p className="summaryValue">{formatMoney(customPx?.prices?.dealerValuation, true)}</p>
                            <p className="summaryDescription">
                                {t(
                                    addPersonalizeOfferSuffixToTranslationKey(
                                        'basket.dealerPx.summary.trade.description'
                                    )
                                )}
                            </p>
                        </div>
                        {!shouldIgnorePXBonus && customPx?.prices?.dealerBonusAmount > 0 && (
                            <span className="icon">+</span>
                        )}
                        {!shouldIgnorePXBonus && customPx?.prices?.dealerBonusAmount > 0 && (
                            <div className="rightSection">
                                <span className="summaryTitle">
                                    {t(
                                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.summary.bonus.title')
                                    )}
                                </span>
                                <p className="summaryValue">{formatMoney(customPx?.prices?.dealerBonusAmount, true)}</p>
                                <p className="summaryDescription">
                                    {t(
                                        addPersonalizeOfferSuffixToTranslationKey(
                                            'basket.dealerPx.summary.bonus.description'
                                        )
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="footer">
                        <span className="label">
                            {t(addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.summary.legalText.title'))}
                        </span>
                        <p className="description">
                            {t(
                                addPersonalizeOfferSuffixToTranslationKey(
                                    'basket.dealerPx.summary.legalText.description'
                                )
                            )}
                        </p>
                    </div>
                </>
            )}
            {!isUserLoggedInAsDealer && (
                <BasketSelectSection
                    leftSectionTitle={t(
                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.selection.yes.title')
                    )}
                    leftSectionDescription={t(
                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.selection.yes.description')
                    )}
                    rightSectionTitle={t(
                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.selection.no.title')
                    )}
                    rightSectionDescription={t(
                        addPersonalizeOfferSuffixToTranslationKey('basket.dealerPx.selection.no.description')
                    )}
                    leftValue={CUSTOM_PX_VALUES.YES}
                    rightValue={CUSTOM_PX_VALUES.NO}
                    leftSelected={customPx?.vehicle?.isActive}
                    rightSelected={!customPx?.vehicle?.isActive}
                    onClick={handleChangeSelection}
                />
            )}
            {isUserLoggedInAsDealer && (
                <Button
                    onClick={handleEditValues}
                    secondary
                    hasEditIcon
                    withoutBackground={isBrandAC}
                    fitContentWidth
                    centerMode
                    marginBottom={20}
                >
                    <IconTemplate name={Icons.edit} />
                    {t('modal.personalizeOffer.edit.button')}
                </Button>
            )}
        </div>
    );
};

export default DealerPartExchangeTemplate;
